import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { api } from "@/api/instance";
import type { IntentOut, ReleaseReason } from "@/api/client";

import { decideLaunch } from "./decide";
import { deviceId } from "./deviceId";
import { watchMicrophoneAccess, type MicrophoneAccess } from "./microphone";
import { qk } from "@/shared/api/queryKeys";

const INTENTS_KEY = qk.launch.intents();
const POLL_INTERVAL_MS = 10_000;
const START_TIMEOUT_MS = 300_000;
const NO_INTENTS: readonly IntentOut[] = [];

export interface UseLaunchIntentsOptions {
  readonly enabled: boolean;
  readonly busy: boolean;
  readonly onLaunch: (intent: IntentOut) => void;
}

export interface LaunchIntentsState {
  readonly confirmStarted: () => void;
  readonly fail: (reason: ReleaseReason) => void;
  readonly adopt: (intentId: string) => Promise<IntentOut | null>;
}

export function useLaunchIntents(
  options: UseLaunchIntentsOptions,
): LaunchIntentsState {
  const { enabled, busy, onLaunch } = options;
  const queryClient = useQueryClient();
  const [ownedId, setOwnedId] = React.useState<string | null>(null);
  const [access, setAccess] = React.useState<MicrophoneAccess>("unknown");
  const ownedIdRef = React.useRef<string | null>(null);
  const claimingRef = React.useRef(false);
  const watchdogRef = React.useRef<number | null>(null);
  const onLaunchRef = React.useRef(onLaunch);
  const accessRef = React.useRef(access);
  const takeRef = React.useRef<(intent: IntentOut) => Promise<void>>(
    async () => undefined,
  );

  const setOwned = React.useCallback((intentId: string | null) => {
    ownedIdRef.current = intentId;
    if (intentId === null) claimingRef.current = false;
    setOwnedId(intentId);
  }, []);

  React.useEffect(() => {
    onLaunchRef.current = onLaunch;
    accessRef.current = access;
  });

  React.useEffect(() => watchMicrophoneAccess(setAccess), []);

  const { data } = useQuery({
    queryKey: INTENTS_KEY,
    queryFn: ({ signal }) => api.v1.listIntents({ signal }).then((r) => r.data),
    enabled,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });
  const intents = data?.intents ?? NO_INTENTS;

  // Derive: if the server no longer lists our owned intent, drop local ownership.
  // Doing this during render (instead of an effect) avoids cascading setState.
  const ownedStillPresent =
    ownedId === null ||
    !data ||
    data.intents.some((item) => item.intent_id === ownedId);
  if (!ownedStillPresent) {
    // State-only: ownedIdRef / claimingRef sync on the next take/release/confirm.
    setOwnedId(null);
  }

  const refresh = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: INTENTS_KEY });
  }, [queryClient]);

  const clearWatchdog = React.useCallback(() => {
    if (watchdogRef.current !== null) {
      window.clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  const { mutateAsync: claimAsync } = useMutation({
    mutationFn: async (intentId: string) => {
      const claimed = await api.v1.claimIntent(intentId, {
        device_id: deviceId(),
      });
      return claimed.data;
    },
    onSettled: () => {
      refresh();
    },
  });

  const { mutate: releaseMutate } = useMutation({
    mutationFn: async ({
      intentId,
      reason,
    }: {
      intentId: string;
      reason: ReleaseReason;
    }) => {
      await api.v1.releaseIntent(intentId, {
        device_id: deviceId(),
        reason,
      });
    },
    retry: 2,
    meta: { silent: true },
    onSettled: () => {
      refresh();
    },
  });

  const { mutate: confirmStartedMutate } = useMutation({
    mutationFn: async (intentId: string) => {
      await api.v1.confirmStarted(intentId, { device_id: deviceId() });
    },
    retry: 2,
    meta: { silent: true },
    onSettled: () => {
      refresh();
    },
  });

  const release = React.useCallback(
    (intentId: string, reason: ReleaseReason) => {
      clearWatchdog();
      claimingRef.current = false;
      setOwned(null);
      releaseMutate({ intentId, reason });
    },
    [clearWatchdog, releaseMutate, setOwned],
  );

  const take = React.useCallback(
    async (intent: IntentOut) => {
      if (claimingRef.current) return;
      claimingRef.current = true;
      try {
        await claimAsync(intent.intent_id);
      } catch {
        claimingRef.current = false;
        return;
      }
      setOwned(intent.intent_id);
      watchdogRef.current = window.setTimeout(() => {
        release(
          intent.intent_id,
          accessRef.current === "denied"
            ? "microphone_denied"
            : "recorder_failed",
        );
      }, START_TIMEOUT_MS);
      onLaunchRef.current(intent);
    },
    [claimAsync, release, setOwned],
  );

  React.useEffect(() => {
    takeRef.current = take;
  }, [take]);

  const adopt = React.useCallback(
    async (intentId: string): Promise<IntentOut | null> => {
      if (claimingRef.current) return null;
      claimingRef.current = true;
      try {
        const claimed = await claimAsync(intentId);
        setOwned(intentId);
        return claimed;
      } catch {
        claimingRef.current = false;
        return null;
      }
    },
    [claimAsync, setOwned],
  );

  const confirmStarted = React.useCallback(() => {
    clearWatchdog();
    const intentId = ownedIdRef.current;
    if (!intentId) return;
    claimingRef.current = false;
    confirmStartedMutate(intentId);
  }, [clearWatchdog, confirmStartedMutate]);

  const fail = React.useCallback(
    (reason: ReleaseReason) => {
      const intentId = ownedIdRef.current;
      if (intentId) release(intentId, reason);
    },
    [release],
  );

  React.useEffect(() => clearWatchdog, [clearWatchdog]);

  const decision = decideLaunch({
    intents,
    access,
    busy,
    ownedIntentId: ownedId,
  });
  const autoStartId =
    decision.kind === "auto_start" ? decision.intent.intent_id : null;
  const lastAutoStartIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!autoStartId) {
      lastAutoStartIdRef.current = null;
      return;
    }
    if (lastAutoStartIdRef.current === autoStartId) return;
    const target = intents.find((item) => item.intent_id === autoStartId);
    if (!target) return;
    lastAutoStartIdRef.current = autoStartId;
    void takeRef.current(target);
  }, [autoStartId, intents]);

  return { confirmStarted, fail, adopt };
}
